const DEFAULT_COOLDOWN_SECONDS = 30;

const clampPositiveInt = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return null;
  return Math.max(1, Math.floor(num));
};

export const formatCooldown = (seconds) => {
  const safeSeconds = clampPositiveInt(seconds) || DEFAULT_COOLDOWN_SECONDS;
  if (safeSeconds < 60) return `${safeSeconds}s`;
  const minutes = Math.floor(safeSeconds / 60);
  const rem = safeSeconds % 60;
  if (!rem) return `${minutes}m`;
  return `${minutes}m ${rem}s`;
};

export const parseRetryAfterSeconds = (retryAfterValue) => {
  if (!retryAfterValue) return null;
  const direct = clampPositiveInt(retryAfterValue);
  if (direct) return direct;
  const dateMs = Date.parse(String(retryAfterValue));
  if (!Number.isFinite(dateMs)) return null;
  const diffSeconds = Math.ceil((dateMs - Date.now()) / 1000);
  return clampPositiveInt(diffSeconds);
};

export const parseFetchRetryAfterSeconds = (response) =>
  parseRetryAfterSeconds(response?.headers?.get?.("Retry-After"));

export const parseAxiosRetryAfterSeconds = (error) =>
  parseRetryAfterSeconds(error?.response?.headers?.["retry-after"]);

export const buildRateLimitMessage = (seconds, subject = "requests") => {
  const cooldown = formatCooldown(seconds);
  return `You're sending ${subject} too quickly. Please wait ${cooldown} before trying again.`;
};

export const isLikelyTimeoutError = (error) => {
  const name = String(error?.name || "").toLowerCase();
  const code = String(error?.code || "").toLowerCase();
  const message = String(error?.message || "").toLowerCase();
  return (
    name.includes("abort") ||
    code.includes("timeout") ||
    message.includes("timeout") ||
    message.includes("networkerror") ||
    message.includes("failed to fetch")
  );
};

export const buildTimeoutMessage = (subject = "request") =>
  `That ${subject} timed out. Please try again in a few seconds.`;

const parseFieldErrors = (data) => {
  if (!data || typeof data !== "object" || Array.isArray(data)) return "";
  const parts = [];
  for (const [key, value] of Object.entries(data)) {
    if (key === "detail" || key === "error") continue;
    const messages = Array.isArray(value) ? value : [value];
    for (const message of messages) {
      if (typeof message === "string" && message.trim()) {
        parts.push(message.trim());
      }
    }
  }
  return parts.join(" ");
};

export const getActionableErrorMessage = (
  error,
  {
    subject = "request",
    on429Subject = null,
    generic = "We couldn't complete that request. Please try again.",
    includeSupportHint = false,
  } = {}
) => {
  const status = Number(error?.response?.status || 0);
  const data = error?.response?.data;
  const statusDetail =
    data?.detail ||
    data?.error ||
    (typeof data === "string" ? data : "");
  const fieldDetail = parseFieldErrors(data);

  if (status === 429) {
    return buildRateLimitMessage(
      parseAxiosRetryAfterSeconds(error),
      on429Subject || `${subject}s`
    );
  }

  if (status === 503 || status === 502 || status === 504) {
    return `Dople is temporarily unavailable. Please retry in about 30 seconds.`;
  }

  if (status === 401) {
    return `Your session expired. Please sign in again and retry.`;
  }

  if (status === 403) {
    return `You don't have access to this action right now. Refresh and try again.`;
  }

  if (isLikelyTimeoutError(error)) {
    return buildTimeoutMessage(subject);
  }

  if (status >= 400 && (statusDetail || fieldDetail)) {
    const message = statusDetail || fieldDetail;
    return `${message}${includeSupportHint ? " If this keeps happening, contact support with the time of this request." : ""}`;
  }

  return generic;
};

